import { useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { personName } from '@/shared/model/person';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';

import { useApproveClassification, useReviewQueue } from '../api/reviewQueue';
import type { ReviewRow } from '../model/review';

/* The PII review queue, as the design draws it: one card, a row per dataset awaiting a human
 * decision, approvable from the keyboard.
 *
 * **A for approve, ↑↓ to move**, and the header says so. Each row is a stop in the tab order, so a
 * reviewer working down a long queue never reaches for the mouse — and A on a row with no verdict
 * does nothing, for the reason its disabled button gives.
 *
 * **Proposed is lavender** because a model proposed it: the same mark the rest of the platform gives
 * anything a model wrote and a person has not yet accepted. Pending is blue, stale amber, and unknown
 * a hollow grey dot — the classifier produced nothing, which is not a verdict of any colour.
 *
 * **A dataset this screen cannot name says why**, with the three causes kept apart: the catalogue
 * could not be read, the name lookup stopped at its bound, or the dataset genuinely is not in the
 * listing. A raw UUID in the cell a reviewer reads first is the defect this platform has shipped four
 * times.
 */

export function ClassificationReview(): ReactElement {
  const { t } = useTranslation();
  const queue = useReviewQueue();
  const approval = useApproveClassification();
  const names = useDatasetNames();
  /* What the last approval did, for the live region — the design's toast, said where a screen reader
     hears it, without a toast system to carry it. */
  const [said, setSaid] = useState('');

  const rows = queue.data ?? [];
  const nameOf = (id: string): string | null => names.data?.byId.get(id) ?? null;

  const approve = (row: ReviewRow): void => {
    if (!row.canApprove || approval.isPending) return;
    const name = nameOf(row.datasetId);
    approval.mutate(row.datasetId, {
      onSuccess: () => {
        setSaid(t('review.approved', { name: name ?? t('review.name.theDataset') }));
      },
    });
  };

  const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, row: ReviewRow): void => {
    /* Only when the row itself has focus: A typed into the row's button or link is that control's. */
    if (event.target !== event.currentTarget) return;
    const key = event.key.toLowerCase();
    if (key === 'a') {
      event.preventDefault();
      approve(row);
    } else if (key === 'arrowdown' || key === 'arrowup') {
      const next =
        key === 'arrowdown'
          ? event.currentTarget.nextElementSibling
          : event.currentTarget.previousElementSibling;
      if (next instanceof HTMLElement) {
        event.preventDefault();
        next.focus();
      }
    }
  };

  return (
    <>
      <PageHeader
        title={t('review.title')}
        help={t('help.feature.piiReview')}
        lede={queue.isSuccess ? t('review.subtitle', { count: rows.length }) : undefined}
        actions={
          <div className="lq-rv__keys" aria-hidden="true">
            <span>
              <kbd className="lq-rv__kbd">A</kbd> {t('review.key.approve')}
            </span>
            <span>
              <kbd className="lq-rv__kbd">↑↓</kbd> {t('review.key.move')}
            </span>
          </div>
        }
      />

      {queue.isError ? (
        <Alert tone="error">
          {t('review.unreachable', { detail: queue.error.message })}
        </Alert>
      ) : null}

      {approval.isError ? (
        /* The server's own words. "cannot approve a verdict computed against a different schema
           state" is actionable — the 409 a reviewer hits when the schema moved while their screen
           was open. */
        <Alert tone="error">{approval.error.message}</Alert>
      ) : null}

      {queue.isPending ? (
        <span
          className="lq-skeleton__bar lq-rv__loading"
          role="status"
          aria-label={t('review.loading')}
        />
      ) : null}

      {queue.isSuccess ? (
        <div className="lq-rv__card">
          <table className="lq-rv__table">
            <caption className="lq-visually-hidden">{t('review.tableCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('review.column.dataset')}</th>
                <th scope="col">{t('review.column.state')}</th>
                <th scope="col">{t('review.column.pii')}</th>
                <th scope="col">{t('review.column.why')}</th>
                <th scope="col">
                  <span className="lq-visually-hidden">{t('review.column.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const name = nameOf(row.datasetId);
                const busy = approval.isPending && approval.variables === row.datasetId;
                return (
                  <tr
                    key={row.datasetId}
                    className="lq-rv__row"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      onRowKey(event, row);
                    }}
                  >
                    <td>
                      {name === null ? (
                        <span className="lq-rv__unnamed">
                          {names.isError
                            ? t('review.name.unread')
                            : names.data?.truncated === true
                              ? t('review.name.truncated')
                              : t('review.name.unlisted')}
                        </span>
                      ) : (
                        <>
                          <DatasetObjectTypeIcon
                            objectType={names.data?.kindById.get(row.datasetId)}
                          />
                          <Link className="lq-rv__name" to={`/datasets/${row.datasetId}`}>
                            {name}
                          </Link>
                        </>
                      )}
                    </td>
                    <td>
                      <span
                        className="lq-rv__state"
                        data-state={row.state}
                        title={t(`review.state.${row.state}.meaning`)}
                      >
                        <span className="lq-rv__dot" aria-hidden="true" />
                        {t(`review.state.${row.state}.label`)}
                      </span>
                    </td>
                    <td>
                      <div className="lq-rv__count">
                        {row.findings.length > 0
                          ? t('review.pii.columns', { count: row.findings.length })
                          : row.claimsNoPii
                            ? t('review.pii.none')
                            : t('review.pii.noVerdict')}
                      </div>
                      {/* Column *and* kind, one per line: a column named `notes` judged to hold an
                          Aadhaar number is exactly the case where the name alone says nothing. */}
                      {row.findings.map((finding) => (
                        <div className="lq-rv__finding" key={finding.column}>
                          <span className="lq-mono">{finding.column}</span> ·{' '}
                          {t(finding.labelKey)}
                        </div>
                      ))}
                    </td>
                    <td className="lq-rv__why">
                      {row.approvedBy !== null
                        ? t('review.changedSince', { who: personName(row.approvedBy) })
                        : (row.detail ?? '—')}
                    </td>
                    <td className="lq-rv__action">
                      <button
                        type="button"
                        className="lq-btn lq-btn--primary lq-btn--compact"
                        disabled={!row.canApprove || busy}
                        title={
                          row.canApprove
                            ? t('review.approveTitle')
                            : t('review.cannotApprove')
                        }
                        onClick={() => {
                          approve(row);
                        }}
                      >
                        {t('review.approve')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 ? (
            <div className="lq-rv__empty">
              <p className="lq-rv__emptyTitle">{t('review.emptyTitle')}</p>
              <p className="lq-rv__emptyBody">{t('review.empty')}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="lq-rv__note">{t('review.changeNote')}</p>
      <p className="lq-visually-hidden" role="status">
        {said}
      </p>
    </>
  );
}
